import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import lodashCloneDeep from 'lodash/fp/cloneDeep';
import lodashGet from 'lodash/get';
import lodashUniq from 'lodash/fp/uniq';
import moment from 'moment';

import typeCheck from 'common/util/typeCheck';
import parseJSON from 'common/util/parseJSON';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { SR_API_ACTION_AGENT_SUBMIT_TOUR_REPORT } from 'api/actionTypes';

import H1 from 'client/components/H1';
import SrAgentToursRow from 'client/components/SrAgentToursRow';

import branch from 'client/core/branch';
import AgentActions from 'client/actions/Agent';
import postWithState from 'client/actions/postWithState';
import safeLocalStorage from 'client/lib/safeLocalStorage';

const TOUR_SECTION_TODAY = 'TOUR_SECTION_TODAY';
const TOUR_SECTION_TOMORROW = 'TOUR_SECTION_TOMORROW';
const TOUR_SECTION_INCOMPLETE = 'TOUR_SECTION_INCOMPLETE';

const createToursSections = (tours = []) => {
  const tourSections = [];
  const startDate = momentWithAustinTimezone().startOf('day');
  const endDate = momentWithAustinTimezone().endOf('day');

  const tourSectionsById = tours.reduce((memo, tour) => {
    const tourStartDate = momentWithAustinTimezone(tour.startDate);
    let tourSectionId = '';

    if (tourStartDate.isSameOrAfter(startDate) && tourStartDate.isBefore(endDate)) {
      tourSectionId = TOUR_SECTION_TODAY;
    } else if (tourStartDate.isSameOrAfter(endDate)) {
      tourSectionId = TOUR_SECTION_TOMORROW;
    } else {
      tourSectionId = TOUR_SECTION_INCOMPLETE;
    }

    return {
      ...memo,
      [tourSectionId]: [...(memo[tourSectionId] || []), tour],
    };
  }, {});

  if (tourSectionsById[TOUR_SECTION_TODAY]) {
    tourSections.push({
      id: TOUR_SECTION_TODAY,
      title: "Today's Tours",
      tours: tourSectionsById[TOUR_SECTION_TODAY],
    });
  }

  if (tourSectionsById[TOUR_SECTION_TOMORROW]) {
    tourSections.push({
      id: TOUR_SECTION_TOMORROW,
      title: "Tomorrow's Tours",
      tours: tourSectionsById[TOUR_SECTION_TOMORROW],
    });
  }

  if (tourSectionsById[TOUR_SECTION_INCOMPLETE]) {
    tourSections.push({
      id: TOUR_SECTION_INCOMPLETE,
      title: 'Incomplete Tours',
      tours: tourSectionsById[TOUR_SECTION_INCOMPLETE],
    });
  }

  return tourSections;
};

const isSameDay = (startDate) => moment(startDate).isSame(moment(), 'day');

@branch({
  tours: ['agent', 'tours'],
  tourGuideReportsByTourId: ['agent', 'tourGuideReportsByTourId'],
  userAgent: ['analytics', 'userAgent'],
  listingPhotoResolution: ['listingPhotoResolution'],
})
class SrAgentToursView extends Component {
  static propTypes = {
    agentUserId: PropTypes.number.isRequired,
    tours: PropTypes.array,
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    userAgent: PropTypes.object.isRequired,
    listingPhotoResolution: PropTypes.number.isRequired,
  };

  constructor(props) {
    super(props);

    const tourGuideReportsByTourIdFromLocalStorage = safeLocalStorage
      ? safeLocalStorage.getItem('tourGuideReportsByTourId')
      : null;

    const tourGuideReportEditsByTourId =
      parseJSON(tourGuideReportsByTourIdFromLocalStorage) || props.tourGuideReportsByTourId;

    this.state = {
      contractedToursByTourId: {},
      tourGuideReportEditsByTourId: lodashCloneDeep(tourGuideReportEditsByTourId),
      includeTours: [],
      lastUpdated: momentWithAustinTimezone().format('LTS'),
    };
    this._updateInterval = null;
  }

  componentDidMount() {
    this._initializeTourEditState(this.props.tours);
    this._updateInterval = global.setInterval(() => {
      this._loadAgentTours();
    }, 30000);
  }

  componentWillUnmount() {
    const { tourGuideReportEditsByTourId } = this.state;

    this.props.dispatch(AgentActions.setTourGuideReportValues, {
      tourGuideReportsByTourId: tourGuideReportEditsByTourId,
    });

    if (this._updateInterval) {
      global.clearInterval(this._updateInterval);
      this._updateInterval = null;
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this._initializeTourEditState(nextProps.tours);
  }

  _initializeTourEditState(tours) {
    if (!tours) {
      return null;
    }
    const { contractedToursByTourId, tourGuideReportEditsByTourId } = this.state;

    const newTourGuideReportEditsByTourId = { ...tourGuideReportEditsByTourId };
    const newContractedToursByTourId = { ...contractedToursByTourId };

    // Set initial values for "notes" textarea components, set initial tour expanded/contracted UI view state.
    let shouldUpdateState = false;
    for (const tour of tours) {
      if (!(tour.id in newTourGuideReportEditsByTourId)) {
        const renterQuestionsByListingId = {};
        const agentCommentsByListingId = {};
        const negativeRenterCommentsByListingId = {};

        tour.listings.forEach((listing) => {
          renterQuestionsByListingId[listing.id] = [];
          agentCommentsByListingId[listing.id] = '';
          negativeRenterCommentsByListingId[listing.id] = '';
        });

        newTourGuideReportEditsByTourId[tour.id] = {
          notes: tour.notes,
          renterName: tour.users[0].name,
          renterQuestionsByListingId: renterQuestionsByListingId,
          agentCommentsByListingId: agentCommentsByListingId,
          negativeRenterCommentsByListingId: negativeRenterCommentsByListingId,
        };

        shouldUpdateState = true;
      }
      if (!(tour.id in newContractedToursByTourId) && (tour.notes || !isSameDay(tour.startDate))) {
        newContractedToursByTourId[tour.id] = true;
        shouldUpdateState = true;
      }
    }

    if (shouldUpdateState) {
      this.setState({
        tourGuideReportEditsByTourId: newTourGuideReportEditsByTourId,
        contractedToursByTourId: newContractedToursByTourId,
      });
    }
  }

  _loadAgentTours = (options = {}) => {
    const { agentUserId, dispatch } = this.props;
    const { includeTours } = this.state;
    const { resetViewingTour } = options;

    dispatch(AgentActions.getAgentTours, {
      agentUserId,
      resetViewingTour,
      includeTours,
    });

    this.setState({
      lastUpdated: momentWithAustinTimezone().format('LTS'),
    });
  };

  _toggleTourExpandedView = (tourId) => {
    const { contractedToursByTourId } = this.state;
    this.setState({
      contractedToursByTourId: {
        ...contractedToursByTourId,
        [tourId]: !contractedToursByTourId[tourId],
      },
    });
  };

  _onTourValuesChange = (tourId, updates) => {
    const { tourGuideReportEditsByTourId } = this.state;
    this.setState({
      tourGuideReportEditsByTourId: {
        ...tourGuideReportEditsByTourId,
        [tourId]: {
          ...tourGuideReportEditsByTourId[tourId],
          ...updates,
        },
      },
    });
  };

  _onSubmitReport = (tourId) => {
    const { dispatch } = this.props;
    const { tourGuideReportEditsByTourId } = this.state;

    const newNotes = tourGuideReportEditsByTourId[tourId].notes.trim();
    const newRenterName = tourGuideReportEditsByTourId[tourId].renterName.trim();
    const newRenterQuestions = tourGuideReportEditsByTourId[tourId].renterQuestionsByListingId;
    const newAgentComments = tourGuideReportEditsByTourId[tourId].agentCommentsByListingId;
    const newNegativeRenterComments =
      tourGuideReportEditsByTourId[tourId].negativeRenterCommentsByListingId;
    typeCheck('newNotes::String', newNotes);

    this._toggleTourExpandedView(tourId);

    dispatch(postWithState, {
      action: SR_API_ACTION_AGENT_SUBMIT_TOUR_REPORT,
      queryId: `${tourId}`,
      payload: {
        tourId: tourId,
        notes: newNotes,
        renterName: newRenterName,
        renterQuestions: newRenterQuestions,
        agentComments: newAgentComments,
        negativeRenterComments: newNegativeRenterComments,
      },
    }).then(() => {
      this.setState(
        {
          includeTours: lodashUniq([tourId, ...this.state.includeTours]),
        },
        () => this._loadAgentTours({ resetViewingTour: true }),
      );
    });
  };

  render() {
    const { history, tours, agentUserId, userAgent, listingPhotoResolution } = this.props;

    const { contractedToursByTourId, tourGuideReportEditsByTourId, lastUpdated } = this.state;

    const tourSections = createToursSections(tours);
    return (
      <div className="contentContainer">
        {tourSections.map(({ id, title, tours }) => {
          return (
            <div
              className={classnames('tourDayContainer', {
                incomplete: TOUR_SECTION_INCOMPLETE === id,
              })}
              key={id}
            >
              <H1>{`${title} (${tours.length})`}</H1>
              <div className="lastUpdated">
                {id === TOUR_SECTION_TODAY && <span>last auto refresh - {lastUpdated}</span>}
              </div>
              {tours.map((tour) => (
                <SrAgentToursRow
                  key={tour.id}
                  tour={tour}
                  notes={
                    lodashGet(tourGuideReportEditsByTourId, `${tour.id}.notes`) || tour.notes || ''
                  }
                  renterName={
                    lodashGet(tourGuideReportEditsByTourId, `${tour.id}.renterName`) ||
                    tour.users[0].name
                  }
                  listingQuestions={
                    lodashGet(
                      tourGuideReportEditsByTourId,
                      `${tour.id}.renterQuestionsByListingId`,
                    ) || {}
                  }
                  agentUserId={agentUserId}
                  expanded={!contractedToursByTourId[tour.id]}
                  onTourValueChange={this._onTourValuesChange}
                  onToggleTourExpandedView={this._toggleTourExpandedView}
                  onSubmitReport={this._onSubmitReport}
                  history={history}
                  userAgent={userAgent}
                  listingPhotoResolution={listingPhotoResolution}
                />
              ))}
            </div>
          );
        })}
      </div>
    );
  }
}

SrAgentToursView.displayName = 'SrAgentToursView';

export default SrAgentToursView;
